<template>
  <modal :show="showModal" modal-classes="modal-secondary" size="lg">
    <div class="row">
      <div class="col-12">
        <base-input
          :label="`${$t('SALES_ORDERS.EVENT_NAME')}*`"
          :placeholder="$t('SALES_ORDERS.EVENT_NAME')"
          v-model="event_name"
          required
        >
        </base-input>
      </div>
    </div>

    <div class="row">
      <div class="col-6">
        <base-input
          :label="`${$t('SALES_ORDERS.EVENT_DATE')}*`"
          :placeholder="$t('SALES_ORDERS.EVENT_DATE')"
          required
        >
          <flat-picker
            :config="{
              allowInput: true,
              minDate: 'today',
              locale: $flatPickrLocale(),
            }"
            class="form-control datepicker"
            v-model="event_date"
            @on-change="() => {}"
          >
          </flat-picker>
        </base-input>
      </div>
      <div class="col-6" v-if="showEventTime">
        <base-input
          :label="`${$t('SALES_ORDERS.EVENT_TIME')}*`"
          :placeholder="$t('SALES_ORDERS.EVENT_TIME')"
          required
        >
          <el-select
            v-model="event_time"
            :placeholder="$t('SALES_ORDERS.EVENT_TIME')"
            class="w-100"
            filterable
            clearable
          >
            <el-option
              v-for="time in timeOptions"
              :key="time.value"
              :label="time.label"
              :value="time.value"
            >
            </el-option>
          </el-select>
        </base-input>
      </div>
    </div>

    <h4 class="mt-2">{{ $t("SALES_ORDERS.DELIVERY_LOCATION") }}</h4>
    <html-editor v-model="delivery_location" @change="onFormChanged()">
    </html-editor>

    <div class="row mt-2">
      <div class="col-12">
        <base-input :label="`${$t('SALES_ORDERS.SELECT_CUSTOMERS')}`">
          <customer-selector
            :allowNone="false"
            :customer="selectedRecipientId"
            :filterable="true"
            :showAll="false"
            :filterOrganization="establishment.organization.id"
            :bypassScope="true"
            @customerChanged="onRecipientChanged"
          />
        </base-input>
      </div>
    </div>

    <div class="row mt-2">
      <div class="col-12">
        <base-input
          :label="`${$t('SALES_ORDERS.PURCHASE_ORDER_NUMBER')}`"
          :placeholder="$t('SALES_ORDERS.PURCHASE_ORDER_NUMBER')"
          v-model="purchase_order_number"
        >
        </base-input>
      </div>
    </div>

    <div class="row mt-2">
      <div class="col-12">
        <label class="form-control-label">{{ $t("SALES_ORDERS.PRICING_TYPE") }}</label>
        <el-radio-group v-model="pricing_type">
          <el-radio :label="PRICING_TYPE_STANDARD">{{ $t("SALES_ORDERS.PRICING_TYPE_STANDARD") }}</el-radio>
          <el-radio :label="PRICING_TYPE_SUBSIDIZED">{{ $t("SALES_ORDERS.PRICING_TYPE_SUBSIDIZED") }}</el-radio>
          <el-radio :label="PRICING_TYPE_CATERER">{{ $t("SALES_ORDERS.PRICING_TYPE_CATERER") }}</el-radio>
        </el-radio-group>
      </div>
    </div>

    <h4 class="mt-2">
      {{ $t("SALES_ORDERS.ALLERGIES_AND_FOOD_INTOLERANCES") }}
    </h4>
    <html-editor
      v-model="allergies_and_food_intolerances"
      @change="onFormChanged()"
    >
    </html-editor>

    <template slot="footer">
      <button
        type="submit"
        class="btn btn-primary"
        @click="addSalesOrder"
        :disabled="loading"
      >
        <span v-if="loading" class="btn-inner--icon">
          <i class="fas fa-spinner fa-spin"></i>
        </span>
        {{ $t("ESTABLISHMENTS.ADD_SALES_ORDER") }}
      </button>

      <button
        type="button"
        class="btn btn-link ml-auto"
        @click="closeModal"
        :disabled="loading"
      >
        {{ $t("COMMON.CLOSE") }}
      </button>
    </template>
  </modal>
</template>

<script>
import moment from "moment";
import swal from "sweetalert2";
import { mapGetters } from "vuex";
import { cloneDeep } from "lodash";
import flatPicker from "vue-flatpickr-component";
import { TimeSelect, Select, Option, Radio, RadioGroup } from "element-ui";
import formMixin from "@/mixins/form-mixin";
import HtmlEditor from "@/components/Inputs/HtmlEditor";
import CustomerSelector from "@/components/CustomerSelector.vue";
import defaultSalesOrder from "../../../SalesModule/SalesOrderManagement/defaultSalesOrder";
import {
  ESTABLISHMENT_TYPE_COLLEGE,
  ESTABLISHMENT_TYPE_UNIVERSITY,
} from "../../../../../constants/establishments";
import {
  PRICING_TYPE_STANDARD,
  PRICING_TYPE_SUBSIDIZED,
  PRICING_TYPE_CATERER,
  pricingTypeHasEventTime,
} from "../../../../../constants/pricingTypes";

export default {
  name: "estbalishment-view-sales-orders-add-sales-order-modal",

  components: { 
    flatPicker, 
    HtmlEditor, 
    CustomerSelector,
    [TimeSelect.name]: TimeSelect,
    [Select.name]: Select,
    [Option.name]: Option,
    [Radio.name]: Radio,
    [RadioGroup.name]: RadioGroup,
  },

  mixins: [formMixin],

  props: ["establishment", "showModal"],

  data() {
    return {
      loading: false,
      event_date: null,
      event_time: null,
      event_name: null,
      delivery_location: null,
      allergies_and_food_intolerances: null,
      purchase_order_number: null,
      pricing_type: PRICING_TYPE_STANDARD,
      PRICING_TYPE_STANDARD,
      PRICING_TYPE_SUBSIDIZED,
      PRICING_TYPE_CATERER,
      selectedRecipientId: null,
      selectedRecipient: null,
    };
  },

  computed: {
    /** Aide alimentaire : pas d'heure d'évènement à saisir. */
    showEventTime() {
      return pricingTypeHasEventTime(this.pricing_type);
    },

    ...mapGetters({
      organizationConfig: "organizationOptions/organizationConfig",
    }),
    
    timeOptions() {
      const options = [];
      for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 15) {
          const timeValue = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
          const hour12 = hour % 12 || 12;
          const ampm = hour < 12 ? 'AM' : 'PM';
          const timeLabel = `${timeValue} (${String(hour12).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${ampm})`;
          options.push({ value: timeValue, label: timeLabel });
        }
      }
      return options;
    },

    defaultRecipient() {
      return (
        (this.establishment &&
          this.establishment.organization &&
          this.establishment.organization.customer) ||
        this.establishment.customer ||
        null
      );
    },
  },

  created() {
    this.setDefaultRecipient();
  },

  methods: {
    onRecipientChanged(customerId, customer) {
      this.selectedRecipientId = customerId;
      this.selectedRecipient = customer || null;
    },

    setDefaultRecipient() {
      if (this.defaultRecipient && this.defaultRecipient.id) {
        this.selectedRecipientId = this.defaultRecipient.id;
        this.selectedRecipient = this.defaultRecipient;
      } else {
        this.selectedRecipientId = null;
        this.selectedRecipient = null;
      }
    },

    async addSalesOrder() {
      if (!this.event_name) {
        this.$notify({
          type: "danger",
          message: `${this.$t("SALES_ORDERS.EVENT_NAME")} ${this.$t(
            "ERRORS.IS_REQUIRED"
          )}`,
        });
        return;
      }

      if (!this.event_date) {
        this.$notify({
          type: "danger",
          message: `${this.$t("SALES_ORDERS.EVENT_DATE")} ${this.$t(
            "ERRORS.IS_REQUIRED"
          )}`,
        });
        return;
      }

      if (this.showEventTime && !this.event_time) {
        this.$notify({
          type: "danger",
          message: `${this.$t("SALES_ORDERS.EVENT_TIME")} ${this.$t(
            "ERRORS.IS_REQUIRED"
          )}`,
        });
        return;
      }

      swal.fire({
        allowEscapeKey: false,
        allowOutsideClick: false,
        onOpen: () => {
          swal.showLoading();
        },
      });
      await this.$store.dispatch(
        "organizationOptions/getOrganizationConfig",
        this.establishment.organization.id
      );

      this.loading = true;
      const salesOrderData = cloneDeep(defaultSalesOrder);
      delete salesOrderData.salesInvoice;
      salesOrderData.expiration_time = moment(
        salesOrderData.expiration_time
      ).toISOString();

      const recipient = this.selectedRecipient || this.defaultRecipient || null;
      const recipientId =
        this.selectedRecipientId ||
        (recipient && recipient.id ? recipient.id : null);

      salesOrderData.has_no_taxes = ![
        ESTABLISHMENT_TYPE_UNIVERSITY,
        ESTABLISHMENT_TYPE_COLLEGE,
      ].includes(this.establishment.establishment_type);
      salesOrderData.recipient = { type: "customers", id: recipientId };
      salesOrderData.organization = this.establishment.organization;
      salesOrderData.allowedLocations = this.establishment.allowedLocations;
      salesOrderData.billing_entity_type =
        (recipient && recipient.customer_type) ||
        this.establishment.billing_entity_type;
      salesOrderData.billing_company_name =
        (recipient && recipient.company_name) ||
        this.establishment.billing_company_name;
      salesOrderData.billing_firstname =
        (recipient && recipient.firstname) ||
        this.establishment.billing_firstname;
      salesOrderData.billing_lastname =
        (recipient && recipient.lastname) ||
        this.establishment.billing_lastname;
      salesOrderData.billing_country =
        (recipient && recipient.country) || this.establishment.billing_country;
      salesOrderData.billing_state =
        (recipient && recipient.state) || this.establishment.billing_state;
      salesOrderData.billing_city =
        (recipient && recipient.city) || this.establishment.billing_city;
      salesOrderData.billing_zipcode =
        (recipient && recipient.zipcode) || this.establishment.billing_zipcode;
      salesOrderData.billing_address =
        (recipient && recipient.address) || this.establishment.billing_address;
      salesOrderData.billing_email =
        (recipient && recipient.email) || this.establishment.billing_email;
      salesOrderData.terms_conditions =
        this.organizationConfig.DEFAULT_SALES_ORDER_TERMS;
      salesOrderData.description =
        this.organizationConfig.DEFAULT_SALES_ORDER_DESCRIPTION;
      salesOrderData.paymentTerm = {
        type: "payment-terms",
        id: this.organizationConfig.DEFAULT_SALES_PAYMENT_TERMS,
      };
      salesOrderData.allergies_and_food_intolerances =
        this.allergies_and_food_intolerances;
      salesOrderData.purchase_order_number = this.purchase_order_number;
      salesOrderData.pricing_type = this.pricing_type;

      delete salesOrderData.cafeteria;
      salesOrderData.establishment = {
        type: "establishments",
        id: this.establishment.id,
      };
      salesOrderData.serviceCenter = {
        type: "service-centers",
        id: this.establishment.serviceCenter.id,
      };
      if (this.establishment.has_warehouse) {
        salesOrderData.sourceWarehouse = {
          type: "warehouses",
          id: this.establishment.warehouse.id,
        };
      } else {
        delete salesOrderData.sourceWarehouse;
      }

      // Sans heure (aide alimentaire), la date n'est qu'une référence de
      // période : même convention de fin de journée que la soumission de
      // coupons, pour que la date de facturation proposée reste le bon jour.
      salesOrderData.event_date = this.showEventTime
        ? moment(
            this.event_date + " " + this.event_time,
            "YYYY-MM-DD HH:mm"
          ).toISOString()
        : moment(this.event_date, "YYYY-MM-DD").endOf("day").toISOString();
      salesOrderData.delivery_location = this.delivery_location;
      salesOrderData.event_name = this.event_name;

      try {
        await this.$store.dispatch("salesOrders/add", salesOrderData);
        this.$notify({
          type: "success",
          message: this.$t("SALES_ORDERS.SALES_ORDER_ADDED"),
        });
        const salesOrder = await this.$store.getters["salesOrders/salesOrder"];
        this.$router.push({
          name: "View SalesOrder",
          params: { id: salesOrder.id },
        });
        this.loading = false;
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
        this.formErrors = error.response.data.errors;
        this.loading = false;
      }
      swal.close();
    },

    closeModal() {
      this.$emit("onCloseModal");
    },
  },

  mounted() {},

  watch: {
    establishment() {
      this.setDefaultRecipient();
    },
  },
};
</script>
